import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "@/components/states";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ChevronLeft, Loader2, Star } from "lucide-react";
import { progressFor } from "./programs.index";

export const Route = createFileRoute("/_app/programs/$curriculumId/")({ component: SubjectPage });

type Topic = {
  id: string;
  unit_id: string | null;
  title: string;
  status: string;
  knowledge_score: number;
  exam_date: string | null;
  position: number;
};

const NEXT: Record<string, string> = {
  not_started: "in_progress",
  in_progress: "done",
  done: "not_started",
};

function statusIcon(status: string) {
  if (status === "done") return "✓";
  if (status === "in_progress") return "◐";
  return "○";
}

function SubjectPage() {
  const { curriculumId } = Route.useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["curriculum", curriculumId],
    queryFn: async () => {
      const [{ data: cur }, { data: units }, { data: topics }] = await Promise.all([
        supabase.from("curricula").select("*").eq("id", curriculumId).maybeSingle(),
        supabase.from("curriculum_units").select("id,title,position").eq("curriculum_id", curriculumId).order("position"),
        supabase
          .from("curriculum_topics")
          .select("id,unit_id,title,status,knowledge_score,exam_date,position")
          .eq("curriculum_id", curriculumId)
          .order("position"),
      ]);
      return { cur, units: units ?? [], topics: (topics ?? []) as Topic[] };
    },
  });

  async function cycleStatus(topic: Topic) {
    await supabase
      .from("curriculum_topics")
      .update({ status: NEXT[topic.status] ?? "in_progress" })
      .eq("id", topic.id);
    await qc.invalidateQueries({ queryKey: ["curriculum", curriculumId] });
    await qc.invalidateQueries({ queryKey: ["curricula"] });
  }

  if (isLoading) {
    return <div className="px-5 pt-10"><LoadingState /></div>;
  }
  if (isError) {
    return <div className="px-5 pt-10"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  }

  const pct = progressFor(data?.topics ?? []);

  return (
    <div className="space-y-5 px-5 pt-8 pb-6">
      <Link to="/programs" className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> {t("programs.title")}
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {data?.cur?.emoji ?? "📘"} {data?.cur?.subject_name}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("programs.annualProgram")}
          {data?.cur?.teacher ? ` · ${data.cur.teacher}` : ""}
          {data?.cur?.school_year ? ` · ${data.cur.school_year}` : ""}
        </p>
        <Progress value={pct} className="h-2" />
        <div className="text-xs text-muted-foreground">{pct}% · {t("programs.completed")}</div>
      </header>

      <p className="text-xs text-muted-foreground">{t("programs.legend")}</p>

      <Accordion type="multiple" defaultValue={(data?.units ?? []).slice(0, 1).map((u) => u.id)} className="space-y-2">
        {(data?.units ?? []).map((u, i) => {
          const topics = (data?.topics ?? []).filter((tp) => tp.unit_id === u.id);
          const upct = progressFor(topics);
          return (
            <Card key={u.id} className="card-soft overflow-hidden rounded-3xl border-0 px-4">
              <AccordionItem value={u.id} className="border-0">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex-1 text-left">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {t("programs.unit")} {i + 1}
                    </div>
                    <div className="text-sm font-semibold">{u.title}</div>
                    <Progress value={upct} className="mt-2 h-1.5 w-32" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-1 pb-3">
                  {topics.map((tp) => (
                    <div key={tp.id} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => cycleStatus(tp)}
                        aria-label={t(`programs.status.${tp.status}`)}
                        className="w-7 shrink-0 rounded-lg py-1 text-center text-sm text-primary hover:bg-primary/10"
                      >
                        {statusIcon(tp.status)}
                      </button>
                      <Link
                        to="/programs/$curriculumId/$topicId"
                        params={{ curriculumId, topicId: tp.id }}
                        className="min-w-0 flex-1 rounded-xl px-2 py-2 text-sm hover:bg-muted/60"
                      >
                        <span className="truncate">{tp.title}</span>
                        {tp.exam_date && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-xs text-amber-600">
                            <Star className="h-3 w-3 fill-current" />
                            {new Date(tp.exam_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                          </span>
                        )}
                      </Link>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Card>
          );
        })}
      </Accordion>
    </div>
  );
}
